import { useLocation, Link } from "react-router-dom";
import { SITE } from "../../data/site";
import { useI18n } from "../../i18n";
import { pageKeyFromSlug, getLocalizedSlug, isTranslated } from "../../data/localizedRoutes";

// EN | FR | DE selector.
// - Preserves the current page when a translation exists.
// - Maps language-specific slugs (doctor-review, aftercare) correctly.
// - Falls back to the translated homepage when the equivalent page is not yet
//   translated, instead of showing a mixed-language / broken route.
export default function LanguageSwitcher({ className = "", onNavigate }) {
  const { pathname } = useLocation();
  const { lang } = useI18n();

  // Strip existing language prefix to get the base (English-structured) path.
  let basePath = pathname;
  if (basePath === "/fr" || basePath.startsWith("/fr/")) basePath = basePath.slice(3) || "/";
  else if (basePath === "/de" || basePath.startsWith("/de/")) basePath = basePath.slice(3) || "/";
  if (basePath === "") basePath = "/";

  const seg = basePath.replace(/^\/|\/$/g, "");
  const localizedKey = pageKeyFromSlug(seg);

  const targetFor = (code) => {
    const prefix = code === "en" ? "" : `/${code}`;
    if (localizedKey) return `${prefix}/${getLocalizedSlug(localizedKey, code)}`;
    if (code === "en") return basePath === "/" ? "/" : basePath;
    if (basePath === "/") return prefix || "/"; // translated homepage always reachable
    if (isTranslated(basePath, code)) return `${prefix}${basePath}`;
    return prefix || "/"; // fallback: translated homepage
  };

  return (
    <div className={`flex items-center gap-1 font-sans text-sm ${className}`} data-testid="language-switcher" aria-label="Select language">
      {SITE.languages.map((l, i) => {
        const active = l.code === lang;
        return (
          <span key={l.code} className="flex items-center">
            {i > 0 && <span className="text-border mx-1.5" aria-hidden="true">|</span>}
            <Link
              to={targetFor(l.code)}
              onClick={onNavigate}
              aria-current={active ? "true" : undefined}
              data-testid={`lang-${l.code}`}
              className={`tracking-wide transition-colors ${
                active ? "text-accent font-semibold" : "text-primary/70 hover:text-primary"
              }`}
            >
              {l.label}
            </Link>
          </span>
        );
      })}
    </div>
  );
}
